<template>
    <div :class="[`${getClassName('wrapper')}`, 'autocomplete-wrapper', classes.wrapper]">
        <div :class="['dropdown', { 'is-active': isActive }]">
            <div class="dropdown-trigger">
                <emp-tag
                    :id="id"
                    :label="label"
                    :tags="tags"
                    :placeholder="placeholder"
                    fieldClass=""
                    :overrideTagInput="true"
                    @dblclick="handleDoubleClick"
                    @blur="handleBlur"
                    @keydown="handleKeyDown"
                    @focus="handleFocus"
                    @keyup="handleInput"
                    @keyup.ctrl.67="handleInput"
                    :name="name"
                    ref="input"
                />
            </div>

            <div :class="`${getClassName('list')} dropdown-menu`" role="menu" v-show="isActive">
                <div class="dropdown-content">
                    <a
                        v-for="(data, i) in json"
                        :class="activeClass(i)"
                        class="dropdown-item"
                        href="#"
                        @click.prevent="selectList(data)"
                        @mousemove="mousemove(i)"
                        :key="i"
                    >
                        <div v-if="onShouldRenderChild">{{ onShouldRenderChild(data) }}</div>
                        <div v-if="!onShouldRenderChild">
                            <b class="autocomplete-anchor-text">{{ deepValue(data, config.anchorFrom) }}</b>
                            <span class="autocomplete-anchor-label" v-if="config.labelFrom">{{
                                deepValue(data, config.labelFrom)
                            }}</span>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/*
     Autocomplete accepts next props
     config (Object)
     Consists of url, anchorFrom, labelFrom, param, customParams, process,
     Example of config
     {
     // url is required
     // url parameter can be Api object or url endpoint
     url: ProductApi,
     // anchorFrom is required
     // anchorFrom is String and it is used as nam
     anchorFrom: 'partNumber'  - String,
     // labelFrom is optional
     // labelFrom is String and it is used as description
     labelFrom: 'partNumber'  String,
     // param is required
     // param is String and it is main parameter which is used for search
     param: 'part_number',
     // customParams is optional
     // customParams is Object of custom parameters which will be passed to query, or we can include some of objects
     customParams: {
     category_id: 10,
     includes:[
     'manufacturer'
     ],
     },
     // process is optional
     // Function to process the API result with. Should return an array of entries or an object whose properties can be enumerated.
     // data - retrived array
     // value - searched keyword
     process: (data, val)=>{}
     },

     configTemplate (String)
     Name of the predefined templates for autocomplete
     (patientNameWithForStock,patientName, partNumDescriptionManualOrExtraOrPriceList, partNumDescription, partId, partNum, partNumFacilityBasedOrderHistory, partNumFacilityBasedInventory, PartNumFacilityShoppingList)

     min (Number: 0)
     Minimum input typed chars before performing the search query. default is 0

     filterByAnchor (Boolean: true)
     When you're using options props, you can have autocomplete to filter your data. Or you can just show your data directly without any filter from autocomplete. The options will be filtered by anchor and it according to the user input.

     encodeParams (Boolean: true)
     Autocomplete will encodeURIComponent all your params before ajax send, When this props sets to true. Default is true #35

     debounce (Number)
     Delay time before do the ajax for the data

     placeholder (String)
     Placeholder for input

     className (String)
     Custom class name for autocomplete component

     classes (Object)
     Spesific custom class for each part. available: wrapper, input, list, and item

     id (String)
     Custom id name for autocomplete component

     debounce (number)
     Number of milliseconds the user should stop typing for before the request is sent. Default is 0, meaning all requests are sent immediately.
     */

import _ from 'lodash';
import Base from 'api/endpoints/base';
import axios from 'axios';

const BLANK_CONFIG = {
    anchorFrom: false,
    labelFrom: false,
    url: false,
    param: 'q',
    customParams: {},
    disabled: false,
};

/*!
     Lot of parts of this component is used from url, some of them are changed to fit our needs.

     https://github.com/BosNaufal/vue2-autocomplete

     Copyright (c) 2016 Naufal Rabbani (http://github.com/BosNaufal)
     * Licensed Under MIT (http://opensource.org/licenses/MIT)
     *
     * Vue 2 Autocomplete @ Version 0.0.1
     *
     */

export default {
    props: {
        id: String,
        name: String,
        className: String,
        classes: {
            type: Object,
            default: () => ({
                wrapper: false,
                input: false,
                list: false,
                item: false,
                label: false,
            }),
        },
        label: {
            type: [String, Boolean],
            default: false,
        },
        placeholder: String,
        tags: {
            type: Array,
            default: () => [],
        },

        // Manual List
        options: Array,

        // Filter After Get the data
        filterByAnchor: {
            type: Boolean,
            default: true,
        },

        config: {
            type: Object,
            validator: (obj) => {
                return obj.anchorFrom && obj.url && obj.param;
            },
        },

        // Debounce time
        debounce: {
            type: Number,
            default: 250,
        },

        // minimum length
        min: {
            type: Number,
            default: 0,
        },

        // Create a custom template from data.
        onShouldRenderChild: Function,

        // Callback
        onInput: Function,
        onShow: Function,
        onBlur: Function,
        onHide: Function,
        onFocus: Function,
        onSelect: Function,
        onBeforeAjax: Function,
        onAjaxProgress: Function,
        onAjaxLoaded: Function,
        onShouldGetData: Function,
    },
    data() {
        return {
            showList: false,
            type: '',
            json: [],
            focusList: '',
            debounceTask: undefined,
        };
    },
    watch: {
        options(newVal, oldVal) {
            if (this.filterByAnchor) {
                const type = this.type;
                const anchorFrom = this.config.anchorFrom;

                const regex = new RegExp(`${type}`, 'i');
                const filtered = newVal.filter((item) => {
                    const found = item[anchorFrom].search(regex) !== -1;
                    return found;
                });
                this.json = filtered;
            } else {
                this.json = newVal;
            }
        },
    },
    methods: {
        getClassName(part) {
            const { classes, className } = this;
            if (classes[part]) return `${classes[part]}`;
            return className ? `${className}-${part}` : '';
        },

        // Get the original data
        cleanUp(data) {
            return JSON.parse(JSON.stringify(data));
        },

        /*==============================
             INPUT EVENTS
             =============================*/
        handleInput(e) {
            if (!e.target) {
                return;
            }

            // Exit early if enter is pressed
            if (e.keyCode == 13) {
                return;
            }

            const { value } = e.target;
            this.showList = true;
            // Callback Event
            if (this.onInput) this.onInput(value);
            // If Debounce
            if (this.debounce) {
                if (this.debounceTask !== undefined) clearTimeout(this.debounceTask);
                this.debounceTask = setTimeout(() => {
                    return this.getData(value);
                }, this.debounce);
            } else {
                return this.getData(value);
            }
            // emit value as input
            this.$emit('input', value);
        },
        handleKeyDown(e) {
            let key = e.keyCode;

            // Disable when list isn't showing up
            if (!this.showList) return;

            // Key List
            const DOWN = 40;
            const UP = 38;
            const ENTER = 13;
            const ESC = 27;

            // Prevent Default for Prevent Cursor Move & Form Submit
            switch (key) {
                case DOWN:
                    e.preventDefault();
                    this.focusList++;
                    break;
                case UP:
                    e.preventDefault();
                    this.focusList--;
                    break;
                case ENTER:
                    e.preventDefault();
                    const isValidIndex = 0 <= this.focusList && this.focusList < this.json.length;
                    if (isValidIndex) {
                        this.selectList(this.json[this.focusList]);
                    }
                    this.showList = false;
                    break;
                case ESC:
                    this.showList = false;
                    break;
            }

            const listLength = this.json.length - 1;
            const outOfRangeBottom = this.focusList > listLength;
            const outOfRangeTop = this.focusList < 0;
            const topItemIndex = 0;
            const bottomItemIndex = listLength;

            let nextFocusList = this.focusList;
            if (outOfRangeBottom) nextFocusList = topItemIndex;
            if (outOfRangeTop) nextFocusList = bottomItemIndex;
            this.focusList = nextFocusList;
        },
        setValue(val) {
            this.type = val;
        },

        /*==============================
             LIST EVENTS
             =============================*/
        handleDoubleClick() {
            this.json = [];
            this.getData('');
            // Callback Event
            this.onShow ? this.onShow() : null;
            this.showList = true;
        },
        handleBlur(e) {
            // Callback Event
            this.onBlur ? this.onBlur(e) : null;
            setTimeout(() => {
                // Callback Event
                this.onHide ? this.onHide() : null;
                this.showList = false;
            }, 250);
        },
        handleFocus(e) {
            this.focusList = 0;
            // Callback Event
            this.onFocus ? this.onFocus(e) : null;
        },
        mousemove(i) {
            this.focusList = i;
        },
        activeClass(i) {
            const focusClass = i === this.focusList ? 'is-active' : '';
            return `${focusClass}`;
        },
        selectList(data) {
            // Deep clone of the original object
            const clean = this.cleanUp(data);
            // Put the selected data to type (model)
            this.tags.push(clean[this.config.anchorFrom]);
            // Hide List
            this.showList = false;
            // Callback Event
            this.onSelect ? this.onSelect(clean) : null;

            // emit value as input
            this.$emit('input', this.type);
        },
        deepValue(obj, path) {
            const arrayPath = path.split('.');
            for (var i = 0; i < arrayPath.length; i++) {
                if (obj.hasOwnProperty(arrayPath[i])) {
                    obj = obj[arrayPath[i]];
                }
            }
            return obj;
        },

        /*==============================
             AJAX EVENTS
             =============================*/

        composeParams(val) {
            const encode = (val) => (this.encodeParams ? encodeURIComponent(val) : val);
            let params = {};
            params[this.config.param] = val;
            if (this.config.customParams) {
                params = _.merge({}, params, this.config.customParams);
            }
            return params;
        },
        async doAjax(val) {
            if (val) {
                // Callback Event
                this.onBeforeAjax ? this.onBeforeAjax(val) : null;
                // Compose Params
                let params = this.composeParams(val);

                if (this.config.url instanceof Object) {
                    let ajaxCall = this.config.url;
                    let result = await ajaxCall.get(params, { hideLoader: true });
                    let resultData = this.getDataFromResponse(result);
                    this.json = this.config.process ? this.config.process(resultData, val) : resultData;
                } else {
                    let url = Base.getApiUrl(this.config.url);
                    let result = await axios.get(url, { hideLoader: true, params });
                    let resultData = this.getDataFromResponse(result);
                    this.json = this.config.process ? this.config.process(resultData, val) : resultData;
                }
            }
        },
        getData(value) {
            if (value.length < this.min || !this.config.url) return;
            if (this.onShouldGetData) this.manualGetData(value);
            else this.doAjax(value);
        },
        getDataFromResponse(response) {
            if ('data' in response) {
                return this.getDataFromResponse(response.data);
            } else {
                return response;
            }
        },

        // Do Ajax Manually, so user can do whatever he want
        manualGetData(val) {
            const task = this.onShouldGetData(val);
            if (task && task.then) {
                return task.then((options) => {
                    this.json = options;
                });
            }
        },
    },
    computed: {
        isActive() {
            return this.showList && this.json.length;
        },
    },
};
</script>

<style lang="scss" scoped>
.autocomplete-wrapper {
    .dropdown {
        display: block;
        min-width: 100%;
        position: relative;
    }

    .autocomplete-input {
        resize: none;
    }

    .dropdown-content {
        display: block !important;
        max-height: 25vh;
        overflow-y: auto;
        overflow-x: hidden;
        z-index: 50;
        min-width: 100%;
    }
    .dropdown-menu {
        width: 100%;
    }
}
</style>
